import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, MapPin, Mail, Phone, Clock } from "lucide-react";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { toast } from "sonner";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESIDENCE_OPTIONS = ["De La Vie Maison", "Heritage", "Hearthstead Cottage"];
const RESIDENCE_SLUGS = {
  "De La Vie Maison": "de-la-vie",
  Heritage: "heritage",
  "Hearthstead Cottage": "hearthstead",
};

const inputCls =
  "w-full bg-transparent border-b border-[#16301f]/25 focus:border-[#16301f] outline-none py-2.5 text-sm text-[#16301f] placeholder:text-[#16301f]/35 transition-colors duration-300";
const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#16301f]/60 mb-1";

export default function Inquiry({ residence, onResidenceChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    check_in: "",
    check_out: "",
    guests: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [blockedMap, setBlockedMap] = useState({});

  useEffect(() => {
    fetch(`${API}/availability`)
      .then((r) => r.json())
      .then(setBlockedMap)
      .catch(() => {});
  }, []);

  const handleDateSelect = (dateStr) => {
    setForm((f) => {
      if (!f.check_in || (f.check_in && f.check_out)) {
        return { ...f, check_in: dateStr, check_out: "" };
      }
      if (dateStr > f.check_in) return { ...f, check_out: dateStr };
      return { ...f, check_in: dateStr, check_out: "" };
    });
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { name: form.name, email: form.email, property: residence };
      if (form.phone) payload.phone = form.phone;
      if (form.check_in) payload.check_in = form.check_in;
      if (form.check_out) payload.check_out = form.check_out;
      if (form.guests) payload.guests = parseInt(form.guests, 10);
      if (form.message) payload.message = form.message;
      await axios.post(`${API}/inquiries`, payload);
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", check_in: "", check_out: "", guests: "", message: "" });
    setSent(false);
  };

  return (
    <section id="inquiry" data-testid="inquiry-section" className="bg-[#f7f2e7]/90 text-[#16301f]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 flex items-center gap-4">
            <span className="h-px w-12 bg-[#16301f]/40 inline-block" />
            Reservations
          </p>
          <h2 className="font-display font-medium text-4xl sm:text-5xl mt-6 leading-[1.08]">Begin your stay</h2>
          <p className="font-script text-3xl sm:text-4xl text-[#1a3324] mt-4 -rotate-1 inline-block">
            We would love to host you.
          </p>
          <p className="text-sm leading-relaxed text-[#16301f]/70 font-light mt-8 max-w-md">
            Tell us which residence you have in mind and when — every enquiry is answered personally,
            with availability and rates tailored to your stay.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <MapPin className="w-5 h-5 text-[#16301f]" strokeWidth={1.5} />
              <p className="text-sm text-[#16301f]/80">Baker &amp; Ascension Parish, Louisiana</p>
            </div>
            <div className="flex items-center gap-4">
              <Mail className="w-5 h-5 text-[#16301f]" strokeWidth={1.5} />
              <a
                data-testid="contact-email-link"
                href="mailto:parkerfurnishedstays@gmail.com"
                className="text-sm text-[#16301f]/80 hover:text-[#16301f] transition-colors duration-300"
              >
                parkerfurnishedstays@gmail.com
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Phone className="w-5 h-5 text-[#16301f]" strokeWidth={1.5} />
              <a
                data-testid="contact-phone-link"
                href="tel:+12254315052"
                className="text-sm text-[#16301f]/80 hover:text-[#16301f] transition-colors duration-300"
              >
                225-431-5052
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Clock className="w-5 h-5 text-[#16301f]" strokeWidth={1.5} />
              <p className="text-sm text-[#16301f]/80">Replies within 24 hours</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="lg:col-span-7"
        >
          <div className="border border-[#16301f]/15 bg-[#fbf8f0] p-6 sm:p-10">
            {sent ? (
              <div data-testid="inquiry-success" className="text-center py-14">
                <span className="mx-auto w-14 h-14 rounded-full border border-[#16301f] flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#16301f]" />
                </span>
                <h3 className="font-display text-3xl mt-6">Enquiry received</h3>
                <p className="text-sm text-[#16301f]/70 font-light mt-3 max-w-sm mx-auto">
                  Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""} — we have received your
                  enquiry for <span className="font-medium">{residence}</span> and will reply within 24
                  hours.
                </p>
                <button
                  data-testid="inquiry-another-btn"
                  onClick={reset}
                  className="mt-8 text-xs uppercase tracking-[0.22em] text-[#16301f]/70 border-b border-[#16301f]/40 pb-1 hover:text-[#16301f] hover:border-[#16301f] transition-colors duration-300"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <>
                <AvailabilityCalendar
                  blocked={new Set(blockedMap[RESIDENCE_SLUGS[residence]] || [])}
                  checkIn={form.check_in}
                  checkOut={form.check_out}
                  onSelect={handleDateSelect}
                />
                <div className="h-px bg-[#16301f]/10 my-8" />
              <form data-testid="booking-inquiry-form" onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label htmlFor="inquiry-name" className={labelCls}>Full Name *</label>
                  <input id="inquiry-name" data-testid="inquiry-name-input" required value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="inquiry-email" className={labelCls}>Email *</label>
                  <input id="inquiry-email" data-testid="inquiry-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="inquiry-phone" className={labelCls}>Phone</label>
                  <input id="inquiry-phone" data-testid="inquiry-phone-input" type="tel" value={form.phone} onChange={update("phone")} placeholder="(225) 000-0000" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="inquiry-residence" className={labelCls}>Residence</label>
                  <select
                    id="inquiry-residence"
                    data-testid="inquiry-residence-select"
                    value={residence}
                    onChange={(e) => onResidenceChange(e.target.value)}
                    className={`${inputCls} cursor-pointer`}
                  >
                    {RESIDENCE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="inquiry-checkin" className={labelCls}>Check-in</label>
                  <input id="inquiry-checkin" data-testid="inquiry-checkin-input" type="date" value={form.check_in} onChange={update("check_in")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="inquiry-checkout" className={labelCls}>Check-out</label>
                  <input id="inquiry-checkout" data-testid="inquiry-checkout-input" type="date" value={form.check_out} onChange={update("check_out")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="inquiry-guests" className={labelCls}>Guests</label>
                  <input id="inquiry-guests" data-testid="inquiry-guests-input" type="number" min="1" max="16" value={form.guests} onChange={update("guests")} placeholder="2" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="inquiry-message" className={labelCls}>Anything we should know?</label>
                  <textarea id="inquiry-message" data-testid="inquiry-message-input" rows={3} value={form.message} onChange={update("message")} placeholder="Occasion, work crew details, special requests..." className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <button
                    data-testid="inquiry-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#16301f] text-[#f7f2e7] text-xs uppercase tracking-[0.25em] px-6 py-4 hover:bg-[#284b35] disabled:opacity-60 transition-colors duration-300"
                  >
                    {sending ? "Sending..." : "Send Enquiry"}
                  </button>
                </div>
              </form>
              </>
            )}
          </div>
          <p className="text-center text-xs text-[#16301f]/50 mt-5">
            Current guest?{" "}
            <a
              data-testid="maintenance-link"
              href="/maintenance"
              className="underline underline-offset-4 decoration-[#16301f]/30 hover:text-[#16301f] transition-colors duration-300"
            >
              Report a maintenance issue
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
